export const SUPPORTED_LANGUAGES = ["en", "fr", "de", "es", "it"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isSupported = (input: unknown): input is SupportedLanguage =>
  typeof input === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(input);

/**
 * Normalize a raw locale string ("fr", "fr-FR", "fr_FR.UTF-8") to one of our
 * supported languages. Returns undefined if no match.
 */
export const normalizeLanguage = (raw: string | null | undefined): SupportedLanguage | undefined => {
  if (!raw) return undefined;
  const head = raw.toLowerCase().split(/[-_;.]/)[0];
  return isSupported(head) ? head : undefined;
};

/**
 * Pick a supported language from an Accept-Language header value like
 * "fr-FR,fr;q=0.9,en;q=0.8". Walks entries in order, returns the first match.
 */
export const pickFromAcceptLanguage = (header: string | null | undefined): SupportedLanguage | undefined => {
  if (!header) return undefined;
  for (const entry of header.split(",")) {
    const tag = entry.split(";")[0]?.trim();
    const matched = normalizeLanguage(tag);
    if (matched) return matched;
  }
  return undefined;
};

/**
 * Client-side detection: cookie -> navigator.language -> "en".
 * Mirror of the AuthLanguageSelector's cookie ("locale") for users who set it.
 */
export const detectClientLanguage = (): SupportedLanguage => {
  if (typeof document !== "undefined") {
    const cookieMatch = document.cookie.match(/(?:^|; )locale=([^;]+)/);
    const fromCookie = normalizeLanguage(cookieMatch?.[1]);
    if (fromCookie) return fromCookie;
  }
  if (typeof navigator !== "undefined") {
    const fromNav = normalizeLanguage(navigator.language);
    if (fromNav) return fromNav;
  }
  return "en";
};
